import { useState, useEffect, useRef, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlineEnvelope, HiXMark } from "react-icons/hi2";
import Loader from "../Loader/Loader";
import MarkdownDescription from "../MarkdownDescription/MarkdownDescription";
import PostcardGenerator from "../PostcardGenerator/PostcardGenerator";
import DeleteModal from "./DeleteModal";
import DetailCarousel from "./DetailCarousel";
import ZoomableImage from "./ZoomableImage";
import { getItem, deleteItem } from "../../utilities/items-service";
import { KIND_LABELS } from "../../utilities/item-kinds";
import { detailAspect } from "../../utilities/framing";
import { formatIssueDate } from "../../utilities/dates";

// Postcard feature hidden for now — flip to true to bring it back
const SHOW_POSTCARD = false;

// Record timestamps display as e.g. "Jan 16, 2025, 4:29 PM"
function formatTimestamp(value) {
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ItemModal({
  catalogueId,
  user,
  onClose,
  onDeleted,
  onPrev,
  onNext,
}) {
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [postcardOpen, setPostcardOpen] = useState(false);
  // Start of an in-progress touch, for swipe prev/next on mobile
  const touchStartRef = useRef(null);

  // Fetch the item whenever the deep-linked catalogue id changes
  useEffect(() => {
    let cancelled = false;
    async function fetchItem() {
      setIsLoading(true);
      setItem(null);
      setPostcardOpen(false);
      const response = await getItem(catalogueId);
      if (cancelled) return;
      if (response && typeof response === "object" && response._id) {
        setItem(response);
      } else {
        console.log("error fetching item:", response);
      }
      setIsLoading(false);
    }
    fetchItem();
    return () => {
      cancelled = true;
    };
  }, [catalogueId]);

  // Lock page scroll while the modal is open; close on Escape,
  // step through the feed with the arrow keys
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function handleKeyDown(evt) {
      if (evt.key === "Escape") {
        onClose();
        return;
      }
      // Leave the arrows alone while typing in a form field
      const tag = evt.target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        evt.target.isContentEditable
      ) {
        return;
      }
      if (evt.key === "ArrowLeft" && onPrev) onPrev();
      if (evt.key === "ArrowRight" && onNext) onNext();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, onPrev, onNext]);

  // Swipe left/right on the panel steps through the feed on touch
  // screens, where the chevron buttons are hidden. Only a decisively
  // horizontal, single-finger swipe counts, so vertical scrolling and
  // pinches are unaffected.
  function handleTouchStart(evt) {
    if (evt.touches.length !== 1) {
      touchStartRef.current = null;
      return;
    }
    touchStartRef.current = {
      x: evt.touches[0].clientX,
      y: evt.touches[0].clientY,
    };
  }

  function handleTouchEnd(evt) {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const dx = evt.changedTouches[0].clientX - start.x;
    const dy = evt.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx < 0) {
      onNext?.();
    } else {
      onPrev?.();
    }
  }

  async function handleDelete() {
    setIsDeleting(true);
    setDeleteError("");
    // deleteItem never throws — it returns the Error on failure
    const result = await deleteItem(item._id);
    if (result instanceof Error) {
      console.log("error deleting item:", result);
      setIsDeleting(false);
      setDeleteError("Could not delete this item. Please try again.");
      return;
    }
    onDeleted?.(item._id);
    onClose();
  }

  const isArticle = item?.kind === "article";
  // Older items only have the single `image` field
  const images = item?.images?.length
    ? item.images
    : item?.image
      ? [item.image]
      : [];
  // Article image captions, keyed by URL (only articles have them)
  const singleImageCaption =
    images.length === 1
      ? item?.imageCaptions?.find((entry) => entry.url === images[0])?.caption
      : null;
  const themeNames = item?.themes?.map((t) => t.themeName) || [];
  const colourNames = item?.colours?.map((c) => c.colourName) || [];

  // A series name also filters the feed, gathering everything issued in that
  // series. `links` replaces the plain `value` when present; `value` still
  // drives the empty check.
  const metadataFields = item
    ? [
        { label: "Catalogue No.", value: item.catalogNumbers },
        { label: "Subject", value: item.subject },
        {
          label: "Series",
          value: item.series?.map((t) => t.seriesName).join(", "),
          links: item.series?.map((t) => ({
            text: t.seriesName,
            to: `/?series=${encodeURIComponent(t.seriesName)}`,
          })),
        },
        {
          label: "Category",
          value: item.category?.map((t) => t.categoryName).join(", "),
        },
        { label: "Denomination", value: item.denomination },
        { label: "Date of issue", value: formatIssueDate(item.year) },
        { label: "Quantity issued", value: item.quantityIssued },
        {
          label: "Themes",
          value: themeNames.length > 0 ? themeNames.join(", ") : null,
        },
        {
          label: "Colours",
          value: colourNames.length > 0 ? colourNames.join(", ") : null,
        },
        {
          label: "Designer",
          value: item.designer?.map((t) => t.designerName).join(", "),
        },
        {
          label: "Designer (stamp)",
          value: item.designerStamp?.map((t) => t.designerName).join(", "),
        },
        {
          label: "Designer (cachet)",
          value: item.designerCachet?.map((t) => t.designerName).join(", "),
        },
        {
          label: "Printing process",
          value: item.printingProcess
            ?.map((t) => t.printingProcessName)
            .join(", "),
        },
      ]
    : [];
  const hasMetadata = metadataFields.some((f) => f.value);

  return (
    <div
      // scrollbar-gutter: stable both-edges reserves the scroll gutter on
      // both sides so a classic scrollbar (Windows) doesn't push the centered
      // panel off the true viewport centre — otherwise the fixed prev/next
      // chevrons, which are positioned off the full viewport, sit unevenly
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto [scrollbar-gutter:stable_both-edges] animate-fadeIn"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-ink/50" onClick={onClose} />

      {/* Prev / next through the feed (arrow keys work too). Sit 40px out
          from the panel edge (half panel width = min(47vw, 448px), button is
          44px wide), but never closer than 12px to the viewport edge */}
      {onPrev && (
        <button
          onClick={onPrev}
          aria-label="Previous item"
          style={{ left: "max(12px, calc(50% - min(47vw, 448px) - 84px))" }}
          className="hidden sm:flex fixed top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-paper/90 border border-ink/15 text-ink/60 hover:text-ink hover:border-ink/40 transition-colors items-center justify-center text-2xl leading-none pb-1"
        >
          &#8249;
        </button>
      )}
      {onNext && (
        <button
          onClick={onNext}
          aria-label="Next item"
          style={{ right: "max(12px, calc(50% - min(47vw, 448px) - 84px))" }}
          className="hidden sm:flex fixed top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-paper/90 border border-ink/15 text-ink/60 hover:text-ink hover:border-ink/40 transition-colors items-center justify-center text-2xl leading-none pb-1"
        >
          &#8250;
        </button>
      )}

      {/* Panel */}
      <div
        className="relative bg-paper rounded-lg shadow-2xl border border-ink/10 w-[94vw] max-w-4xl my-6 sm:my-10"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-paper/90 border border-ink/15 text-ink/60 hover:text-ink hover:border-ink/40 transition-colors flex items-center justify-center"
        >
          <HiXMark className="w-5 h-5" />
        </button>

        {isLoading ? (
          <div className="flex justify-center items-center py-32">
            <Loader />
          </div>
        ) : !item ? (
          <div className="py-24 px-8 text-center">
            <h2 className="font-display text-xl mb-4 text-ink">
              Item not found
            </h2>
            <button
              className="text-sm underline text-ink/60 hover:text-ink"
              onClick={onClose}
            >
              Return to the archive
            </button>
          </div>
        ) : (
          <div className="px-6 sm:px-10 py-8">
            {/* Kind label + admin actions */}
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-seal">
                {KIND_LABELS[item.kind] || "Stamp"}
              </span>
              {user?.isAdmin === true && (
                <span className="ml-auto flex gap-2 mr-10">
                  <button
                    onClick={() => navigate(`/items/${item.catalogue_id}/edit`)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-ink/25 text-ink/70 hover:border-ink/60 hover:text-ink transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setDeleteError("");
                      setShowDeleteModal(true);
                    }}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-seal/40 text-seal hover:bg-seal/10 hover:border-seal transition-colors"
                  >
                    Delete
                  </button>
                </span>
              )}
            </div>

            {isArticle ? (
              <>
                {/* Article: title-first layout */}
                <h2 className="font-display text-3xl sm:text-4xl text-ink leading-tight mb-[21px]">
                  {item.subject || item.catalogue_id}
                </h2>
                {item.year && (
                  <p className="font-mono text-xs text-ink/45 mb-4">
                    {formatIssueDate(item.year)}
                  </p>
                )}
                {/* mb-8 matches the pt-8 between a stamp/cover image and
                    its metadata grid below */}
                {images.length > 1 ? (
                  <div className="mb-8">
                    <DetailCarousel
                      images={images}
                      framing={item.framing?.detail}
                      imageFramings={item.imageFramings}
                      captions={item.imageCaptions}
                      kind={item.kind}
                      alt={item.subject || "Article illustration"}
                    />
                  </div>
                ) : (
                  images.length === 1 && (
                    <div
                      className="group relative mx-auto overflow-hidden rounded-md border border-ink/10 mb-8"
                      style={{
                        aspectRatio: detailAspect("article"),
                        width: "100%",
                        maxWidth: "min(100%, 93vh)",
                      }}
                    >
                      <ZoomableImage
                        src={images[0]}
                        alt={item.subject || "Article illustration"}
                        framing={item.framing?.detail}
                        disabled
                      />
                      {singleImageCaption && (
                        <span className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 max-w-[85%] truncate rounded-full bg-paper/90 border border-ink/10 px-4 py-1.5 font-mono text-xs text-ink shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          {singleImageCaption}
                        </span>
                      )}
                    </div>
                  )
                )}
                {/* Width matches the image/carousel above */}
                <div
                  className="mx-auto w-full px-[10px]"
                  style={{ maxWidth: "min(100%, 93vh)" }}
                >
                  <MarkdownDescription>{item.description}</MarkdownDescription>
                </div>
              </>
            ) : (
              <>
                {/* Stamp / cover: image-first layout. The scan fills a
                    fixed-aspect window framed by the item's detail settings —
                    no mat behind it, since scans keep their own paper
                    background; maxWidth caps the height near the old 62vh */}
                {images.length > 1 ? (
                  <DetailCarousel
                    images={images}
                    framing={item.framing?.detail}
                    imageFramings={item.imageFramings}
                    kind={item.kind}
                    alt={item.subject || KIND_LABELS[item.kind]}
                  />
                ) : (
                  <div
                    className="relative mx-auto overflow-hidden rounded-md border border-ink/10"
                    style={{
                      aspectRatio: detailAspect(item.kind),
                      width: "100%",
                      maxWidth: "min(100%, 93vh)",
                    }}
                  >
                    <ZoomableImage
                      src={images[0]}
                      alt={item.subject || KIND_LABELS[item.kind]}
                      framing={item.framing?.detail}
                      disabled={item.kind !== "cover"}
                    />
                  </div>
                )}

                {/* Width matches the image/carousel above */}
                <div
                  className="mx-auto grid w-full grid-cols-1 md:grid-cols-[calc((100%-48px)/13*5+20px)_1fr] gap-8 md:gap-[38px] px-[10px] pt-8 pb-2"
                  style={{ maxWidth: "min(100%, 93vh)" }}
                >
                  {/* Metadata */}
                  {hasMetadata && (
                    <div>
                      {metadataFields.map(
                        (field) =>
                          field.value && (
                            <p
                              key={field.label}
                              className="text-left leading-snug"
                            >
                              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">
                                {field.label}:
                              </span>{" "}
                              <span className="font-serif text-xs text-ink/80">
                                {field.links
                                  ? field.links.map((link, index) => (
                                      <Fragment key={link.to}>
                                        {index > 0 && ", "}
                                        <Link
                                          to={link.to}
                                          className="underline decoration-ink/25 underline-offset-[3px] hover:text-seal hover:decoration-seal/50 transition-colors"
                                        >
                                          {link.text}
                                        </Link>
                                      </Fragment>
                                    ))
                                  : field.value}
                              </span>
                            </p>
                          ),
                      )}
                    </div>
                  )}

                  {/* Description */}
                  {item.description && (
                    <div className={hasMetadata ? "" : "md:col-span-2"}>
                      <MarkdownDescription>
                        {item.description}
                      </MarkdownDescription>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Postcard generator — stamps only */}
            {SHOW_POSTCARD && item.kind === "stamp" && item.image && (
              <div className="mt-8">
                <button
                  className="cursor-pointer flex justify-center w-full"
                  onClick={() => setPostcardOpen((prev) => !prev)}
                >
                  <span className="inline-flex items-center gap-2.5 px-8 py-3 rounded-full border border-ink/15 bg-cream hover:border-ink/40 hover:shadow-sm text-ink/70 hover:text-ink text-sm font-medium tracking-wide transition-all duration-300 select-none active:scale-[0.97]">
                    <svg
                      className={`w-3.5 h-3.5 transition-transform duration-300 opacity-60 ${postcardOpen ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <HiOutlineEnvelope className="w-4 h-4" />
                    Send a postcard
                  </span>
                </button>
                {postcardOpen && (
                  <div className="mt-6 animate-[fadeSlideIn_0.3s_ease-out]">
                    <PostcardGenerator
                      stampImageUrl={item.image}
                      stampSubject={item.subject}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Record timestamps — admins only. -mb-4 trims the panel's 32px
                bottom padding so the space below matches the 16px above */}
            {user?.isAdmin === true &&
              (item.createdAt || item.releasedAt || item.updatedAt) && (
                <div className="mt-8 pt-4 -mb-4 border-t border-ink/10 flex flex-wrap gap-x-6 gap-y-1">
                  {item.createdAt && (
                    <p className="font-mono text-[11px] text-ink/45">
                      CREATED: {formatTimestamp(item.createdAt)}
                    </p>
                  )}
                  {/* Where the item sits in the feed; reset when unhidden */}
                  {item.releasedAt && (
                    <p className="font-mono text-[11px] text-ink/45">
                      RELEASED: {formatTimestamp(item.releasedAt)}
                    </p>
                  )}
                  {item.updatedAt && (
                    <p className="font-mono text-[11px] text-ink/45">
                      LAST MODIFIED: {formatTimestamp(item.updatedAt)}
                    </p>
                  )}
                </div>
              )}
          </div>
        )}

        {/* Delete confirmation */}
        {showDeleteModal && (
          <DeleteModal
            kind={item?.kind}
            onCancel={() => setShowDeleteModal(false)}
            onConfirm={handleDelete}
            isDeleting={isDeleting}
            error={deleteError}
          />
        )}
      </div>
    </div>
  );
}
